import {useMutation} from '@tanstack/react-query'
import {useNavigate, useParams, useSearchParams} from 'react-router-dom'
import {Error, Success} from 'src/feature/bite/helpers/notification'
import useRequestParams from 'src/feature/bite/hook/request'
import {ContactSchema, StoreListSchemaResponse, StoreSchemaResponse} from '../domain'
import {useStore} from '../hook'
import {API, createDetailResponseSchema} from 'src/feature/bite/domain'
import {toast} from 'react-toastify'
import {getTimeZone} from 'src/feature/bite/helpers/date'
import {useGetDetail} from './useQuery'
// import { useAuthUser } from 'react-auth-kit';

export function useUpdateMerchant() {
  const {auth, headers, errors} = useRequestParams()

  return useMutation(
    ['MerchantCorporateUpdate'],
    async ({values}: {values: any}) => {
      values.id = auth()?.merchantID
      values.userID = auth()?.id
      const timeZoneOffset = getTimeZone()

      const Response = await fetch(`${API}/Merchant/UpdateMerchant`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...values,
          language: headers.Language,
          timeZoneOffset,
        }),
      })

      const Data = await Response.json()

      if (!Response.ok || !Data.responseResult.result) {
        Error(Data?.responseResult?.exceptionDetail || errors.merchant.update_error)
        return false
      }

      Success(errors.merchant.update_success)

      return true
    },
    {
      onSuccess: () => {
        return true
      },
      onError: (error) => {
        Error(errors.merchant.update_error)
        return false
      },
    }
  )
}

export function useUpdateStore({storeId}: {storeId: string}) {
  const {auth, headers, errors} = useRequestParams()
  const {detail, detailSet} = useStore()
  const router = useNavigate()
  const {refetch} = useGetDetail(storeId)

  return useMutation(
    ['StoreUpdate'],
    async ({values}: {values: any}) => {
      const timeZoneOffset = getTimeZone()
      values.seasonStartDate = values.seasonStartDate || '0001-01-01T00:00:00.000Z'
      values.seasonEndDate = values.seasonEndDate || '0001-01-01T00:00:00.000Z'

      const Response = await fetch(`${API}/Store/Update`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...values,
          id: storeId,
          userID: auth().id,
          language: headers.Language,
          timeZoneOffset,
        }),
      })

      const Data = await Response.json()

      if (!Response.ok || !Data.responseResult.result) {
        Error(Data?.responseResult?.exceptionDetail || errors.store.update_error)
        return false
      }

      Success(errors.store.update_success)

      await refetch()

      /*    const ResponseDetail = await fetch(`${API}/Store/Detail`, {
        method: 'POST',
        headers,
        body: JSON.stringify({storeId, userID: auth().id, language: headers.Language}),
      })

      if (ResponseDetail.status === 401) signOut()

      const DetailData = await ResponseDetail.json()
      const safeParse = StoreSchemaResponse.safeParse(DetailData)
      const parse = StoreSchemaResponse.parse(DetailData)

      if (!parse.response.store) {
        router('/error/store')
      }

      if (parse.responseResult.result === false) {
        toast.error(parse.responseResult.exceptionDetail || 'Error', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
        return false
      }

      if (!safeParse.success) {
        Error(errors.store.update_error)
        return false
      }
 */

      return true
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errors.store.update_error)
      },
    }
  )
}

export function useCreateStore() {
  const {auth, headers, errors} = useRequestParams()
  const router = useNavigate()

  return useMutation(
    ['StoreCreate'],
    async ({values}: {values: any}) => {
      values.seasonStartDate = !values.seasonPeriod
        ? '0001-01-01T00:00:00.000Z'
        : values.seasonStartDate
      values.seasonEndDate = !values.seasonPeriod
        ? '0001-01-01T00:00:00.000Z'
        : values.seasonEndDate

      const timeZoneOffset = getTimeZone()

      const Response = await fetch(`${API}/Store/Create`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...values,
          userID: auth().id,
          timeZoneOffset,
          merchantID: auth().merchantID,
          contactMobilePhone: values?.contactMobilePhone
            ? values.contactMobilePhone?.replace(/\D/g, '')?.replace('971', '')
            : '',
          language: headers.Language,
        }),
      })

      const Data = await Response.json()

      if (!Response.ok || !Data.responseResult.result) {
        Error(Data?.responseResult?.exceptionDetail || errors.store.update_error)
        return false
      }

      Success(errors.store.create_success)

      const merchantType = auth()?.merchantBusinessType
      if (merchantType === 1) router(`/stores?tab=locations`)
      else router(`/provider?tab=locations`)

      return true
    },
    {
      onSuccess: () => {
        return true
      },
      onError: (error) => {
        Error(errors.store.update_error)
        return false
      },
    }
  )
}

export function useUpdateStoreContact({storeId}: {storeId: string}) {
  const {auth, headers, errors} = useRequestParams()
  const {updateDetail} = useStore()

  return useMutation(
    ['StoreUpdateContact'],
    async ({input}: {input: any}) => {
      const formattedPhone = input.phone.replace(/\D/g, '').replace('971', '')
      const Response = await fetch(`${API}/Store/StoreContactUpdate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...input,
          phone: formattedPhone,
          storeId,
          userID: auth()?.id,
          language: headers.Language,
        }),
      })

      const Data = await Response.json()

      if (!Response.ok || !Data.responseResult.result) {
        Error(Data?.responseResult?.exceptionDetail || errors.store.update_error)
        return
      }

      Success(errors.store.update_success)

      const ResponseContactDetail = await fetch(`${API}/Store/GetStoreContact`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID: auth()?.id,
          storeId,
        }),
      })

      if (ResponseContactDetail.status === 401) signOut()

      const DataStoreContactUpdate = await ResponseContactDetail.json()

      const ResponseSchema = createDetailResponseSchema(ContactSchema)
      const safeParse = ResponseSchema.safeParse(DataStoreContactUpdate)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = ResponseSchema.parse(DataStoreContactUpdate)

      if (parse.responseResult.result === false) {
        throw new Error(parse.responseResult.exceptionDetail || 'Error')
      }

      updateDetail(parse.response, 'Contact')

      return {
        status: true,
        detail: parse.response,
      }
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errors.store.update_error)
      },
    }
  )
}

export function useStoreOptionalInfoUpdate({storeId}: {storeId: string}) {
  const {auth, headers, errors} = useRequestParams()

  return useMutation(['StoreOptionalInfoUpdate'], async (input: any) => {
    const timeZoneOffset = getTimeZone()

    input.seasonStartDate = !input.seasonPeriod ? '0001-01-01T00:00:00.000Z' : input.seasonStartDate
    input.seasonEndDate = !input.seasonPeriod ? '0001-01-01T00:00:00.000Z' : input.seasonEndDate

    const Response = await fetch(`${API}/Store/StoreOptionalInfoUpdate`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ...input,
        id: storeId,
        storeId,
        userID: auth()?.id,
        language: headers.Language,
        timeZoneOffset,
      }),
    })

    const Data = await Response.json()

    if (!Response.ok || !Data.responseResult.result) {
      toast.error(Data?.responseResult?.exceptionDetail || errors.store.update_error)
      return false
    }

    Success(errors.store.update_success)

    return true
  })
}

export function useUpdateUser() {
  const {storeId} = useParams()
  const [searchParams] = useSearchParams()
  const {auth, headers, errors} = useRequestParams()

  const userID = auth()?.id
  const id = searchParams?.get('userID') || ''

  return useMutation(
    ['StoreUserUpdate'],
    async ({values}: {values: any}) => {
      const Response = await fetch(`${API}/User/UpdateStoreUser`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...values,
          storeId,
          userID,
          id,
          language: headers.Language,
        }),
      })

      const Data = await Response.json()

      if (!Response.ok || !Data.responseResult.result) {
        Error(Data?.responseResult?.exceptionDetail || errors.user.update_error)
        return
      }

      Success(errors.user.update_success)
      return true
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errors.user.update_error)
      },
    }
  )
}

export function useCreateUser() {
  const {auth, headers, errors} = useRequestParams()

  return useMutation(['StoreUserCreate'], async ({values}: {values: any}) => {
    const timeZoneOffset = getTimeZone()

    const Response = await fetch(`${API}/User/AddStoreUser`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ...values,
        // storeId: '996225251718',
        userID: auth()?.id,
        language: headers.Language,
        timeZoneOffset,
      }),
    })

    const Data = await Response.json()

    if (!Response.ok || !Data.responseResult.result) {
      Error(Data?.responseResult?.exceptionDetail || errors.user.create_error)
      return false
    }

    Success(errors.user.create_success)
    return true
  })
}

export function useGetAll() {
  const {auth, headers, errorMessage, signOut} = useRequestParams()
  const {list} = useStore()

  const userID = auth()?.id

  const listFilter = {
    filterList: [],
    orderList: [{type: 2, key: 'CreatedOn'}],
    paging: {
      currentPageNumber: 1,
      pageSize: list.paging.totalCount,
    },
  }

  return useMutation(
    ['storesAll'],
    async () => {
      const timeZoneOffset = getTimeZone()

      const Response = await fetch(`${API}/Store/List`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listFilter,
          userID,
          language: headers.Language,
          timeZoneOffset,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()
      const safeParse = StoreListSchemaResponse.safeParse(Data)
      const parse = StoreListSchemaResponse.parse(Data)

      if (parse.responseResult.result === false) {
        Error(parse.responseResult.exceptionDetail || 'Error')
        return []
      }

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      return parse.response.dataList
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errorMessage)
      },
    }
  )
}

function signOut() {
  throw new Error('Function not implemented.')
}
